/**
 * A component for rendering the top-of-page header bar. This component mainly
 * handles figuring out which components should be in the header depending
 * logged-in/logged-out status and other factors, and passing data to the learn
 * menu, and profile dropdown
 *
 * Note that this is purely a presentation component. Several of the children
 * (such as the user dropdown) depend on their data being loaded asynchronously.
 * Callbacks are provided for a stateful component above this one to know when
 * to load data.
 */
import {t} from "@lingui/core/macro";
import {css, StyleSheet, type CSSProperties} from "aphrodite";
import {compose} from "mu-lambda";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import Clickable from "@khanacademy/wonder-blocks-clickable";
import {View} from "@khanacademy/wonder-blocks-core";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import IconButton from "@khanacademy/wonder-blocks-icon-button";
import Link from "@khanacademy/wonder-blocks-link";
import {actionStyles} from "@khanacademy/wonder-blocks-styles";
import {
    border,
    color,
    fade,
    font,
    mix,
    spacing,
} from "@khanacademy/wonder-blocks-tokens";
import {styles as typographyStyles} from "@khanacademy/wonder-blocks-typography";
import {
    markMinorConversions,
    withMarkConversion,
    type WithMarkConversionProps,
    type WithoutMarkConversion,
} from "@khan/analytics-learning-time";
import {SkipLink} from "@khan/component-skip-link";
import {PermissionsInfo, RequestInfo} from "@khan/shared-core";
import {
    a11yStyles,
    maxContainerWidth,
    mediaQueries,
    pageHeaderHeight,
    pageHeaderHorizontalPadding,
    zindexHeader,
} from "@khan/shared-styles";
import {getLoginUrl, getSignupUrl} from "@khan/urls";

import withApollo, {
    type WithApolloProps,
    type WithoutApollo,
} from "#data-access/gql/with-apollo.tsx";
import {getKaid} from "#data-access/profile-utils.ts";
import withLocation, {
    type WithLocationProps,
    type WithoutLocation,
} from "#data-access/with-location.tsx";
import withUserProfile, {
    type WithoutUserProfile,
    type WithUserProfileProps,
} from "#data-access/with-user-profile.tsx";
// eslint-disable-next-line no-restricted-imports -- TODO: Replace this component with the equivalent one from Wonder Blocks.
import deprecatedReactRender from "#rendering/deprecated-react-render.tsx";
// TODO(TUT-1078): Remove static import once all locales use the same search
import {IsIndexedSearchEnabledForLocale} from "#search-package/indexed-search/is-indexed-search-enabled-for-locale.ts";
import supports from "#shared-package/supports.ts";
import AphroditeCSSTransitionGroup from "#shared-styles-package/aphrodite-css-transition-group.tsx";
import type {BloodhoundT} from "#typeahead-package/types.ts";

import AINavItem from "./ai-nav-item.tsx";
import {BackgroundContext} from "./background-context.ts";
import CoursesMenuContainer from "./courses-menu/courses-menu-container.tsx";
import CoursesMenu from "./courses-menu/courses-menu.tsx";
import LearnMenuTopicsQuery from "./courses-menu/learn-menu-topics-query.ts";
import DonateLink from "./donate-link.tsx";
import {getSkipToTargetMount} from "./get-skip-to-target-mount.ts";
import HeaderLogo from "./header-logo.tsx";
import HeaderSearchBox from "./header-search-box.tsx";
import MaybeShowDonateBanner from "./maybe-show-donate-banner.tsx";
import ResponsiveNavMenu from "./responsive-nav-menu/responsive-nav-menu.tsx";
import responsiveStyles from "./responsive-nav-menu/styles.ts";
import TransitionAppear from "./transition-appear.tsx";
import UserDropdown from "./user-dropdown.tsx";
import {
    getProfileDropdownData,
    TRIGGLESS_MODAL_FOCUS_ANCHOR_ID,
} from "./util.ts";

// SVG icons used in the header
import listIcon from "@phosphor-icons/core/regular/list.svg";
import magnifyingGlassIcon from "@phosphor-icons/core/regular/magnifying-glass.svg";
import xIcon from "@phosphor-icons/core/regular/x.svg";

type SearchBox = typeof import("../typeahead-package/search-box.tsx").default;

const SupportBar = React.lazy(
    () => import("#support-bar/internal/support-bar.tsx"),
);

// A "Subjects" learn menu placeholder used to align both the search form
// and its results.
class LearnMenuSpacer extends React.Component<Record<any, any>> {
    render(): React.ReactNode {
        return (
            <div
                className={css(
                    styles.pad,
                    styles.navItem,
                    styles.learnMenuSpacer,
                )}
            >
                {t`Courses`}
            </div>
        );
    }
}

export type HeaderProps = {
    // Optional: If specified, gives the navbar the dark background
    // color that transitions to white on hover.
    backgroundColor?: "white" | "darkBlue";
    /**
     * Optional: If specified, sets the header to be sticky to the top of the
     * page when scrolled on `mdOrLarger` size viewports. Defaults to false.
     */
    sticky?: boolean;
    // Whether to render the "skip to main" link.
    showSkipToMain: boolean;
} & WithMarkConversionProps &
    WithLocationProps &
    WithUserProfileProps &
    WithApolloProps;

type DefaultProps = {
    showSkipToMain: HeaderProps["showSkipToMain"];
};

type State = {
    // We keep track of which dropdown is open (except for search,
    // which is only open when it is focused and thus closes
    // automatically) so we can ensure only one dropdown is open at a
    // time. Possible values are null (meaning nothing is open),
    // "notifications", "profile", and "learn-menu".
    openMenu:
        | "notifications"
        | "profile"
        | "learn-menu"
        | "user"
        | "ai-nav-item"
        | null;
    // We keep track of whether or not the header is hovered (which
    // happens from right after someone interacts with it until 1
    // second after they interact with it).
    hovered: boolean;
    // Boolean that determines whether or not to show an error message.
    showError: boolean;
    // Keep track of whether the responsive menu is currently open or
    // not.
    responsiveMenuOpen: boolean;
    // Whether or not the desktop-search menu is open.
    searchMenuOpen: boolean;
    hasLoadedTypeahead: boolean;
    SearchBox: SearchBox | null | undefined;
    suggestionSource?: BloodhoundT;
    searchHover: boolean;
    donateModalOpen: boolean;
};

class SiteHeader extends React.Component<HeaderProps, State> {
    searchBoxRef: HTMLAnchorElement | null | undefined;
    userDropdownElement: HTMLElement | null | undefined;
    learnMenuRef: HTMLElement | null | undefined;
    contentWrapperHeight: string;
    _unmountTriggerlessModalFocusAnchor: (() => void) | undefined;
    _unmountSkipToMain: (() => void) | undefined;

    static defaultProps: DefaultProps = {
        showSkipToMain: true,
    };

    constructor(props: HeaderProps) {
        super(props);
        this.contentWrapperHeight = "100%";
    }

    state: State = {
        // We keep track of which dropdown is open (except for search,
        // which is only open when it is focused and thus closes
        // automatically) so we can ensure only one dropdown is open at a
        // time. Possible values are null (meaning nothing is open),
        // "notifications", "profile", and "learn-menu".
        openMenu: null,

        // We keep track of whether or not the header is hovered (which
        // happens from right after someone interacts with it until 1
        // second after they interact with it).
        hovered: false,

        // Keep track of whether the responsive menu is currently open or
        // not.
        responsiveMenuOpen: false,

        // Whether or not the desktop-search menu is open.
        searchMenuOpen: false,

        hasLoadedTypeahead: false,
        SearchBox: null,
        suggestionSource: undefined,
        searchHover: false,
        showError: false,
        donateModalOpen: false,
    };

    componentDidMount() {
        this._isMounted = true;
        // Store the initial contentWrapper height prop for restoration
        // if we open then close the responsive menu.
        // GL-969 Works around a scrolling bug
        const contentWrapper = this.getOuterWrapper();
        this.contentWrapperHeight = contentWrapper.style.height;

        if (typeof document !== "undefined" && document.body) {
            document.body.addEventListener("click", this.handleBodyClick);
        }

        this.addSkipToMainToDOM();

        if (!this.props.profile) {
            return;
        }
    }

    componentWillUnmount() {
        if (typeof document !== "undefined" && document.body) {
            document.body.removeEventListener("click", this.handleBodyClick);
        }
        this._isMounted = false;

        if (this._skipToMainMountNode) {
            const skipToMainLinkMountNode = this._skipToMainMountNode;
            this._skipToMainMountNode = null;
            this._unmountSkipToMain?.();
            // Remove the mount node from the DOM as well
            skipToMainLinkMountNode.remove();
        }
    }

    // TODO(briangenisio): This is a hack to remove the deprecated call to
    // this.isMounted() but is still considered an anti-pattern.  Replace
    // with code which does not rely on the mounted state like Redux or GraphQL
    _isMounted = false;

    // A node that we add as the very first child of the outer-wrapper element.
    // We use this element as a portal to attach the Skip to Main Content button,
    // which screen readers expect to be before the header navigation.
    _skipToMainMountNode: HTMLElement | null | undefined = null;

    handleFocus: () => void = () => {
        // NOTE(jordan): This fixes a strange bug where transparent navs
        // require two clicks to open on mobile for logged out users. The
        // first click merely makes nav opaque, and a second click actually
        // triggers the click event.
        //
        // My understanding is that if mouseenter/mousemove changes the
        // content of the page, onclick will not fire off. In this case, a
        // mouseenter is changing the nav (after setting hovered to true).
        //
        // https://developer.apple.com/library/ios/documentation/AppleApplications/Reference/SafariWebContent/HandlingEvents/HandlingEvents.html
        //
        // Why this is only affecting logged-out users escapes me, but
        // disabling this handler for touch devices fixes the issue and does
        // not affect logged-in users.
        if (supports.touchevents) {
            return;
        }

        if (this._isMounted) {
            this.setState({
                hovered: true,
            });
        }
    };

    handleBlur: (e: React.SyntheticEvent<HTMLElement>) => void = (e) => {
        // If the blur event was triggered again after the search box is
        // closed, ignore it.
        if (!(e.target instanceof HTMLElement)) {
            return;
        }

        const shouldNotBlur = e.target.getAttribute(
            "data-do-not-blur-header-on-search-bar-close",
        );

        if (shouldNotBlur) {
            return;
        }

        if (this._isMounted) {
            this.setState({
                hovered: false,
            });
        }
    };

    // If the user clicks somewhere on the page that isn't one of the
    // dropdowns, we should close any currenly open dropdown.
    handleBodyClick: (event: MouseEvent) => void = (event) => {
        // Body clicks should never close the learn menu if we're on the
        // /welcome page and the learn menu still appears to be a module
        // on the page.
        // Once it's been closed from the toggle by the user,
        // body clicks will close the learn menu as normal.
        if (!this._isMounted || this.state.openMenu === null) {
            return;
        }

        // See if the clicked element is contained within one of our dropdowns.
        // We check all of the current dropdowns, not just the currently open
        // one, because the if we click on the trigger for one of the closed
        // dropdowns then the onclick for that dropdown will close the current
        // dropdown already, and it might want to know which dropdown was open
        // before.
        let testElem: EventTarget | Node | null | undefined = event.target;
        while (testElem !== document.body) {
            if (
                testElem === this.userDropdownElement ||
                testElem === this.learnMenuRef
            ) {
                // One of our dropdowns is an ancestor of the target, so the
                // element being clicked on is inside one of them.
                return;
            }
            if (testElem instanceof HTMLElement) {
                testElem = testElem.parentNode;
            } else {
                return;
            }
        }

        // Close any open menu otherwise, if the target is outside of all
        // dropdowns.
        this.setOpenMenu(null);
    };

    handleUserClick: () => void = () => {
        if (this.state.openMenu !== "user") {
            markMinorConversions([
                {
                    id: "global_nav_open_user_menu",
                    extra: {
                        view: "desktop",
                    },
                },
            ]);

            this.setOpenMenu("user");
        } else {
            this.setOpenMenu(null);
        }
    };

    handleLearnMenuClick: (closingThroughLinkClick?: boolean) => void = (
        closingThroughLinkClick,
    ) => {
        const {publishAnalyticsEvent} = this.props;
        if (this.state.openMenu === "learn-menu") {
            // If we're closing the menu
            this.setOpenMenu(null, closingThroughLinkClick);
        } else {
            // If we're opening the menu
            markMinorConversions([{id: "global_nav_open_learn_menu"}]);

            publishAnalyticsEvent({
                eventType: "NavBarCourseMenuOpened",
                eventSchemaVersion: 1,
                currentPageUrlPath: this.props.location.pathname,
            });

            this.setOpenMenu("learn-menu");
        }
    };

    handleResponsiveMenuKeyUp: (event: React.KeyboardEvent) => void = (
        event,
    ) => {
        if (
            event.key === " " ||
            event.key === "Enter" ||
            (event.key === "Escape" && this.state.responsiveMenuOpen)
        ) {
            event.preventDefault();
            this.handleResponsiveMenuToggle();
        }
    };

    setOpenMenu: (
        openMenu: State["openMenu"],
        closingThroughLinkClick?: boolean,
    ) => void = (openMenu, closingThroughLinkClick = false) => {
        if (!this._isMounted) {
            return;
        }

        // If the argument menu is already open, do nothing
        if (this.state.openMenu === openMenu && this.state.openMenu !== null) {
            return;
        }

        // Perform certain actions depending on which menu is currently open
        if (this.state.openMenu === "learn-menu") {
            this.hideDashboardSpacer();
            if (!closingThroughLinkClick) {
                this.props.publishAnalyticsEvent({
                    eventType: "NavBarCourseMenuClosed",
                    eventSchemaVersion: 1,
                    currentPageUrlPath: this.props.location.pathname,
                });
            }
        }

        this.setState({openMenu});
    };

    handleResponsiveMenuToggle: () => void = () => {
        const responsiveMenuOpen = !this.state.responsiveMenuOpen;
        // HACK: Safari iOS does not respect overflow values on document.body,
        // so we have to do UA sniffing in 2017, folks! For now, just work
        // around it by applying overflow on the main contents instead. We
        // assume this is "outer-wrapper", as specified in page-template.html,
        // but long-term, these types of "take over the whole screen" menu
        // needs a re-work if we can get the whole app to render in a unified
        // React tree.
        const userAgent = window.navigator.userAgent;
        const useHackySafariOverflowWorkaround =
            userAgent.indexOf("Safari") >= 0 &&
            userAgent.indexOf("Mobile") >= 0 &&
            userAgent.indexOf("Chrome") < 0;

        // Disable scrolling if menu will open
        //
        // TODO(jordan): For accessibility purposes, we need to make sure
        // (1) Focus cannot leave the menu while expanded
        // (2) All other content is `aria-hidden`
        const contentWrapper = this.getOuterWrapper();
        if (responsiveMenuOpen) {
            if (useHackySafariOverflowWorkaround && contentWrapper) {
                this.contentWrapperHeight = contentWrapper.style.height;
                contentWrapper.style.height = "100vh";
            }
        } else {
            if (useHackySafariOverflowWorkaround && contentWrapper) {
                contentWrapper.style.overflowY = "auto";
                contentWrapper.style.height = this.contentWrapperHeight;
            }
        }

        if (responsiveMenuOpen) {
            markMinorConversions([{id: "global_nav_open_mobile"}]);
        }

        if (this._isMounted) {
            this.setState({
                responsiveMenuOpen,
            });
        }
    };

    handleSearchClick: (e: React.SyntheticEvent) => void = (e) => {
        e.preventDefault();

        markMinorConversions([
            {
                id: "global_nav_start_search",
                extra: {
                    view: "desktop",
                },
            },
        ]);

        // Hmmm maybe load on hover?
        if (!this.state.hasLoadedTypeahead) {
            const currentLocale = RequestInfo.kaLocale;

            // The new searchbar revamped experience simply passes the query to
            // the search page. That is what search-box-only
            // does. This new experience is only enabled for some locales.
            const importsArray = IsIndexedSearchEnabledForLocale(currentLocale)
                ? [
                      import("../typeahead-package/search-box-only.tsx").then(
                          (module) => module.default,
                      ),
                      import("../typeahead-package/suggestion-source.ts"),
                      import(
                          "../../stylesheets/typeahead-package/typeahead.less"
                      ),
                  ]
                : [
                      import("../typeahead-package/search-box.tsx").then(
                          (module) => module.default,
                      ),
                      import("../typeahead-package/suggestion-source.ts"),
                      import(
                          "../../stylesheets/typeahead-package/typeahead.less"
                      ),
                  ];

            Promise.all(importsArray).then(
                // @ts-expect-error - TS2345 - Argument of type '([SearchBoxModule, { suggestionSource, initSuggestions }]: [any, any]) => void' is not assignable to parameter of type '(value: [typeof SearchBox, typeof import("/Users/kevinbarabash/khan/webapp/services/static/apps/khanacademy/src/typeahead-package/suggestion-source"), any]) => void | PromiseLike<void>'.
                ([SearchBoxModule, {suggestionSource, initSuggestions}]: [
                    any,
                    any,
                ]) => {
                    if (this._isMounted) {
                        // Typeahead suggestions are only available in
                        // non-English locales.
                        if (RequestInfo.kaLocale !== "en") {
                            initSuggestions(this.props.gqlClient);
                        }

                        const TypedSearchBox = SearchBoxModule as SearchBox;

                        this.setState({
                            SearchBox: TypedSearchBox,
                            suggestionSource,
                            hasLoadedTypeahead: true,
                            searchMenuOpen: true,
                            searchHover: false,
                        });
                    }
                },
            );
        } else {
            if (this._isMounted) {
                this.setState({
                    searchMenuOpen: true,
                    searchHover: false,
                });
            }
        }
    };

    handleSearchExit: (e?: React.SyntheticEvent) => void = (e) => {
        e && e.preventDefault();
        if (this._isMounted) {
            this.setState({
                searchMenuOpen: false,
            });
            // TODO(jeff, WEB-1378): Use Wonder Blocks Timing API
            // eslint-disable-next-line no-restricted-syntax
            setTimeout(() => {
                if (this._isMounted && this.searchBoxRef) {
                    this.searchBoxRef.focus();
                }
            });
        }
    };

    /**
     * Used to hide the spacer that pushes the dashboard down beneath the
     * learn menu.
     */
    hideDashboardSpacer: () => void = () => {
        if (typeof document !== "undefined" && document.body) {
            document.body.classList.add("bibliotron-hide-spacer");
        }
    };

    getOuterWrapper() {
        return document.getElementById("outer-wrapper") || document.body;
    }

    getNavItemStyles(): Array<CSSProperties | null | undefined> {
        return [
            styles.pad,
            styles.navItem,
            this.isBackgroundWhite() ? styles.navItemWhenWhite : null,
            styles.wonderBlocksNavItem,
        ];
    }

    getTextNavItemStyles(): Array<CSSProperties | null | undefined> {
        return [
            ...this.getNavItemStyles(),
            styles.textNavItem,
            this.isBackgroundWhite() ? styles.textNavItemWhenWhite : null,
        ];
    }

    getDonateStyles(): Array<CSSProperties | null | undefined> {
        return [...this.getTextNavItemStyles(), responsiveStyles.hideWhenSmall];
    }

    getAiNavItemStyles(): Array<CSSProperties | null | undefined> {
        return [
            ...this.getNavItemStyles(),
            this.state.openMenu === "ai-nav-item"
                ? {
                      boxShadow: `inset 0 -3px 0 0 ${
                          this.getBackgroundColor() === NAV_ITEM_LIGHT_COLOR
                              ? NAV_ITEM_DARK_COLOR
                              : NAV_ITEM_LIGHT_COLOR
                      }`,
                  }
                : undefined,
            styles.hoverUnderline,
        ];
    }

    handleAiNavItemToggle: (opened: boolean) => void = (opened) => {
        if (opened) {
            this.setOpenMenu("ai-nav-item");
        } else {
            this.setOpenMenu(null);
        }
    };

    isBackgroundWhite(): boolean {
        const {backgroundColor} = this.props;
        // The menu is white by default unless we overwrite it with the
        // `backgroundColor` prop.
        if (!backgroundColor || backgroundColor === "white") {
            return true;
        }

        // The menu is white if
        return (
            this.state.openMenu !== null ||
            this.state.responsiveMenuOpen || // some menu is open
            this.state.searchMenuOpen
        ); // or search is active
    }

    getBackgroundColor: () => string = () => {
        if (this.isBackgroundWhite()) {
            return NAV_ITEM_LIGHT_COLOR;
        }
        return color.darkBlue;
    };

    getSearchBackgroundColor: () => string = () => {
        if (this.state.searchHover) {
            return color.offBlack8;
        }
        if (this.isBackgroundWhite()) {
            // When the search field is active, the input field placeholder
            // text becomes a different color, the browser default. In this
            // case, we need to lighten the input field background to have
            // sufficient contrast
            if (this.state.searchMenuOpen) {
                return color.offWhite;
            }
            return color.offWhite;
        }
        if (this.props.backgroundColor) {
            // NOTE: This is the color of the search bar background with the
            // new darkBlue theming of the global navigation bar. This
            // particular color was confirmed with design.
            return mix(fade(color.white, 0.08), color.darkBlue);
        }
        return "transparent";
    };

    markSignupLinkClicked: () => void = () => {
        markMinorConversions([{id: "global_nav_signup_clicked"}]);
    };

    renderProminentSearchContents: () => React.ReactNode = () => {
        // TODO(FEI-5787): Load search asynchronously.
        if (this.state.searchMenuOpen) {
            return (
                <View
                    style={[
                        styles.headerContents,
                        styles.alignLeft,
                        styles.rowReverse,
                    ]}
                >
                    <AphroditeCSSTransitionGroup
                        transitionStyle={{
                            appear: styles.enterSearch,
                            appearActive: styles.enterActiveSearch,
                        }}
                        transitionAppearTimeout={300}
                    >
                        <View
                            style={[
                                styles.searchWrapper,
                                styles.activeSearchWrapper,
                            ]}
                        >
                            <View style={styles.searchBoxContainer}>
                                {this.state.SearchBox ? (
                                    <HeaderSearchBox
                                        onEscape={this.handleSearchExit}
                                        searchFormStyles={[
                                            styles.searchForm,
                                        ].filter((s) => !!s)}
                                        searchInputStyles={[
                                            styles.searchInput,
                                            styles.prominentSearchInput,
                                            styles.wonderBlocksNavItem,
                                        ].filter((s) => !!s)}
                                        SearchBox={this.state.SearchBox}
                                        spacer={this.renderProminentSearchSpacer()}
                                        suggestionSource={
                                            this.state.suggestionSource
                                        }
                                    />
                                ) : null}
                            </View>
                        </View>
                    </AphroditeCSSTransitionGroup>
                    <LearnMenuSpacer />
                    {this.renderCloseSearchLink()}
                </View>
            );
        }
        return (
            <div className={css(styles.headerContents)}>
                <div
                    className={css(
                        styles.headerSection,
                        styles.grow,
                        styles.alignLeft,
                        styles.hideOnPrint,
                    )}
                >
                    {this.renderLearnMenu()}
                    <View style={styles.searchWrapper}>
                        <Button
                            onClick={this.handleSearchClick}
                            testId="navbar-search-button"
                            aria-label={t`Search for courses, skills, and videos`}
                            startIcon={magnifyingGlassIcon}
                            kind="secondary"
                            style={[
                                styles.inactiveSearchButton,
                                !this.isBackgroundWhite() &&
                                    actionStyles.inverse,
                            ]}
                        >
                            {t`Search`}
                        </Button>
                    </View>
                </div>
                <div className={css(styles.headerSection)}>
                    {this.renderLogo()}
                </div>
                <div
                    className={css(
                        styles.headerSection,
                        styles.grow,
                        styles.hideOnPrint,
                        styles.alignRight,
                    )}
                >
                    <BackgroundContext.Provider
                        value={this.isBackgroundWhite()}
                    >
                        <AINavItem
                            profile={this.props.profile}
                            profileLoading={this.props.profileLoading}
                            navItemStyles={[...this.getAiNavItemStyles()]}
                            isResponsiveNavMenu={false}
                            isOpen={this.state.openMenu === "ai-nav-item"}
                            onToggle={(opened) =>
                                this.handleAiNavItemToggle(opened)
                            }
                        />
                    </BackgroundContext.Provider>
                    {PermissionsInfo.isLoggedIn
                        ? this.renderLoggedInItems()
                        : this.renderLoggedOutItems()}
                    {this.renderResponsiveMenuItems()}
                </div>
            </div>
        );
    };

    renderCloseSearchLink: () => React.ReactNode = () => (
        <IconButton
            onClick={this.handleSearchExit}
            testId="header-search-close"
            aria-label="Close search"
            icon={xIcon}
            kind="tertiary"
            size="medium"
        />
    );

    renderSearchResultsSpacer: () => React.ReactNode = () => (
        <div className={css(styles.searchResultsSpacer)}>
            <LearnMenuSpacer />
        </div>
    );

    renderProminentSearchSpacer: () => React.ReactNode = () => (
        <div className={css(styles.prominentSearchResultsSpacer)}>
            <LearnMenuSpacer />
        </div>
    );

    addSkipToMainToDOM() {
        const outerWrapper = this.getOuterWrapper();

        if (!this.props.showSkipToMain || !outerWrapper) {
            return;
        }

        const skipToMountingNode = getSkipToTargetMount();
        outerWrapper.insertBefore(skipToMountingNode, outerWrapper.firstChild);

        // This creates and adds the skip to main content link.
        this._skipToMainMountNode = document.createElement("div");
        skipToMountingNode.appendChild(this._skipToMainMountNode);

        if (this._skipToMainMountNode) {
            // TODO(somewhatabstract): This should be using React portals
            this._unmountSkipToMain = deprecatedReactRender(
                // A "Skip to main content" link for keyboard users to
                // quickly jump past navigation.
                <SkipLink
                    // This ID ensures that this link receives focus when
                    // a deprecated modal closes if there is no other
                    // focused element.
                    id={TRIGGLESS_MODAL_FOCUS_ANCHOR_ID}
                    targetSelector="#main-content"
                    translatedText={t`Skip to main content`}
                    data-skip-to-main
                />,
                this._skipToMainMountNode,
            );
        }
    }

    maybeRenderDonationBanner(): React.ReactNode {
        // The only place we don't try to render this component is the /donate
        // page, since it has a non-banner version of this component already
        // included in the page.
        if (this.props.location.pathname !== "/donate") {
            return <MaybeShowDonateBanner />;
        }
        return null;
    }

    renderLogo: () => React.ReactNode = () => {
        // We don't pass in a transparent prop so that we can manage the logo
        // color ourselves
        // TODO(mdr): We could segment this into desktop/mobile conversions,
        //     but we'd need to render two logos: one for each screen size.
        return (
            <HeaderLogo
                logoStyles={[...this.getNavItemStyles(), styles.logo]}
                conversion="global_nav_visit_home"
                backgroundColor={
                    this.isBackgroundWhite() ? "white" : "darkBlue"
                }
            />
        );
    };

    renderLearnMenu: () => React.ReactNode = () => {
        return (
            <LearnMenuTopicsQuery>
                {({loading, error, domains}) => {
                    return (
                        <CoursesMenu
                            ref={(r) => (this.learnMenuRef = r)}
                            domains={domains}
                            loading={loading}
                            error={error}
                            onDropdownToggle={this.handleLearnMenuClick}
                            onDropdownClose={() => this.setOpenMenu(null)}
                            showDropdown={this.state.openMenu === "learn-menu"}
                            buttonStyles={[
                                this.getTextNavItemStyles(),
                                styles.noPadding,
                            ]}
                            backgroundColor={
                                this.isBackgroundWhite() ? "white" : "darkBlue"
                            }
                            stickyHeader={this.props.sticky}
                            shouldRenderStandardsGuideLink={this.userIsLoggedOutOrTeacher()}
                        />
                    );
                }}
            </LearnMenuTopicsQuery>
        );
    };

    userIsLoggedOutOrTeacher = (): boolean => {
        const {profile, profileLoading} = this.props;

        if (!PermissionsInfo.isLoggedIn) {
            return true;
        }

        if (profileLoading) {
            return false;
        }

        if (profile?.get("isTeacher")) {
            return true;
        }

        return false;
    };

    renderLoginLink(): React.ReactNode {
        return (
            <Link
                href={getLoginUrl()}
                id="login-or-signup"
                key="signin-link"
                rel="nofollow"
                style={[
                    ...this.getTextNavItemStyles(),
                    responsiveStyles.hideWhenSmall,
                ]}
                safeWithNav={() =>
                    markMinorConversions([
                        {
                            id: "global_nav_login_clicked",
                        },
                    ])
                }
            >
                {t`Log in`}
            </Link>
        );
    }

    recordDonateLinkClicked() {
        const {location, publishAnalyticsEvent, profile} = this.props;

        const kaid = getKaid(profile) || "";
        const isLoggedIn = PermissionsInfo.isLoggedIn;
        // TODO: switch to using PermissionsInfo.isPhantom and evaluate
        // whether the implementation of that method is accurate. Not
        // all logged-out users are phantoms (some are pre-phantom).
        const isPhantom = !isLoggedIn;

        // Deprecated: the CEDAR event GlobalNavBarDonateLinkClicked is
        // intended to replace the global_nav_donate_clicked Bingo
        // conversion.
        //
        // At the time of writing it's unclear whether or not there are
        // any existing reports dependent on this conversion, however.
        // Until it can be confirmed that the conversion is no longer in
        // use we'll continue reporting it.
        const conversionOp = markMinorConversions([
            {
                id: "global_nav_donate_clicked",
                // the mobile donate button is in the responsive nav menu
                // which is hidden/shown based off media queries
                extra: {
                    view: "desktop",
                    isLoggedIn,
                    isPhantom,
                    kaid,
                },
            },
        ]);

        const analyticsOp = publishAnalyticsEvent({
            eventType: "GlobalNavBarDonateLinkClicked",
            eventSchemaVersion: 1,
            presentation: "NAV_BAR",
            currentPageURL: location.href,
        });

        return Promise.all([conversionOp, analyticsOp]);
    }

    // Renders the user dropdown (profile / notifications)
    renderLoggedInItems(): React.ReactNode {
        const {profile, publishAnalyticsEvent} = this.props;
        const showDonateLink = RequestInfo.kaLocale === "en";
        const {pathname, hash} = location;

        // TODO(jordan): We need a way to open this (with notifications)
        // from EndOfTaskCard
        const links = [
            <UserDropdown
                key="user-dropdown"
                dropdownRef={(node) => (this.userDropdownElement = node)}
                onDropdownToggle={this.handleUserClick}
                onDropdownClose={() => this.setOpenMenu(null)}
                showDropdown={this.state.openMenu === "user"}
                buttonStyles={[
                    this.getTextNavItemStyles(),
                    styles.hoverUnderline,
                ]}
                currentUrl={pathname + hash}
                signUpUrl={getSignupUrl()}
                profileData={getProfileDropdownData(profile)}
                isLightTheme={this.isBackgroundWhite()}
            />,
        ];

        if (showDonateLink) {
            // @ts-expect-error - TS2345 - Argument of type 'Element[]' is not assignable to parameter of type 'Element'.
            links.unshift([
                <TransitionAppear key="donate-link">
                    <>
                        {profile && (
                            <DonateLink
                                testId="header-donate-link"
                                safeWithNav={() => {
                                    // Deprecated: the CEDAR event
                                    // GlobalNavBarDonateLinkClicked is intended
                                    // to replace the global_nav_donate_clicked
                                    // Bingo conversion.
                                    //
                                    // At the time of writing it's unclear
                                    // whether or not there are any existing
                                    // reports dependent on this conversion,
                                    // however. Until it can be confirmed that
                                    // the conversion is no longer in use we'll
                                    // continue reporting it.
                                    const conversionOp = markMinorConversions([
                                        {
                                            id: "global_nav_donate_clicked",
                                            // the mobile donate button is in the responsive nav menu
                                            // which is hidden/shown based off media queries
                                            extra: {
                                                view: "desktop",
                                                isLoggedIn: true,
                                                isPhantom: false,
                                                kaid: getKaid(profile) || "",
                                            },
                                        },
                                    ]);

                                    const analyticsOp = publishAnalyticsEvent({
                                        eventType:
                                            "GlobalNavBarDonateLinkClicked",
                                        eventSchemaVersion: 1,
                                        presentation: "NAV_BAR",
                                        currentPageURL:
                                            this.props.location.href,
                                    });

                                    return Promise.all([
                                        conversionOp,
                                        analyticsOp,
                                    ]);
                                }}
                                urlParams={{c_src2: "Webapp.header"}}
                                style={this.getDonateStyles()}
                            />
                        )}
                    </>
                </TransitionAppear>,
            ]);
        }

        return links;
    }

    // TODO(diedra): If we keep these separate sign up and in links, we
    // should also put them inside a <ul role="nav"> element because it's
    // a list of navigation links.
    renderLoggedOutItems(): React.ReactNode {
        // NOTE: changes to this list of locales should be approved by the
        // philanthropy department.
        const hideDonateLinkForLocales = ["mr", "hi", "pa"];
        const showDonateLink = !hideDonateLinkForLocales.includes(
            RequestInfo.kaLocale,
        );
        const {profile, publishAnalyticsEvent} = this.props;

        const links = [
            this.renderLoginLink(),

            <Button
                key="signup-link"
                href={getSignupUrl()}
                rel="nofollow"
                style={[responsiveStyles.hideWhenSmall]}
                onClick={this.markSignupLinkClicked}
                size="medium"
            >
                {t`Sign up`}
            </Button>,
        ];

        if (showDonateLink) {
            links.unshift([
                <DonateLink
                    testId="header-donate-link"
                    key="donate-link"
                    safeWithNav={() => {
                        // Deprecated: the CEDAR event
                        // GlobalNavBarDonateLinkClicked is intended
                        // to replace the global_nav_donate_clicked
                        // Bingo conversion.
                        //
                        // At the time of writing it's unclear
                        // whether or not there are any existing
                        // reports dependent on this conversion,
                        // however. Until it can be confirmed that
                        // the conversion is no longer in use we'll
                        // continue reporting it.
                        const conversionOp = markMinorConversions([
                            {
                                id: "global_nav_donate_clicked",
                                // the mobile donate button is in the responsive nav menu
                                // which is hidden/shown based off media queries
                                extra: {
                                    view: "desktop",
                                    isLoggedIn: false,
                                    isPhantom: !PermissionsInfo.isLoggedIn,
                                    kaid: getKaid(profile) || "",
                                },
                            },
                        ]);

                        const analyticsOp = publishAnalyticsEvent({
                            eventType: "GlobalNavBarDonateLinkClicked",
                            eventSchemaVersion: 1,
                            presentation: "NAV_BAR",
                            currentPageURL: this.props.location.href,
                        });

                        return Promise.all([conversionOp, analyticsOp]);
                    }}
                    urlParams={{c_src2: "Webapp.header"}}
                    style={this.getDonateStyles()}
                />,
            ]);
        }

        return links;
    }

    renderResponsiveMenuItems: () => React.ReactNode = () => {
        let items: Array<React.ReactNode> = [];

        if (!location.pathname.startsWith("/search")) {
            items = items.concat([
                <div
                    key="responsive-search"
                    className={css(styles.responsiveMenuItemWrapper)}
                >
                    <Clickable
                        safeWithNav={() =>
                            markMinorConversions([
                                {
                                    id: "global_nav_start_search",
                                    extra: {view: "mobile"},
                                },
                            ])
                        }
                        href="/search"
                        testId="mobile-search-button"
                        style={styles.responsiveMenuLink}
                    >
                        {() => {
                            return (
                                <>
                                    <span className={css(a11yStyles.srOnly)}>
                                        {t`Search for courses, skills, and videos`}
                                    </span>
                                    <PhosphorIcon
                                        icon={magnifyingGlassIcon}
                                        size="medium"
                                        color={
                                            this.isBackgroundWhite()
                                                ? color.blue
                                                : color.white
                                        }
                                    />
                                </>
                            );
                        }}
                    </Clickable>
                </div>,
            ]);
        }

        const buttonLabel = this.state.responsiveMenuOpen
            ? t`Close menu`
            : t`Open menu`;

        return items.concat([
            <View
                key="responsive-dropdown-menu"
                style={styles.responsiveMenuItemWrapper}
            >
                <Clickable
                    onClick={this.handleResponsiveMenuToggle}
                    onKeyUp={this.handleResponsiveMenuKeyUp}
                    style={[
                        ...this.getNavItemStyles(),
                        styles.responsiveMenuLink,
                    ]}
                    testId="mobile-header-menu-button"
                    aria-expanded={this.state.responsiveMenuOpen}
                    aria-label={buttonLabel}
                    aria-controls="page-header-responsive-nav-menu"
                >
                    {() => (
                        <PhosphorIcon
                            icon={
                                this.state.responsiveMenuOpen ? xIcon : listIcon
                            }
                            size="medium"
                        />
                    )}
                </Clickable>
            </View>,
        ]);
    };

    maybeRenderResponsiveNavMenu(): React.ReactNode {
        if (!this.state.responsiveMenuOpen) {
            return null;
        }

        return (
            <LearnMenuTopicsQuery>
                {({loading, error, domains}) => {
                    return (
                        <CoursesMenuContainer loading={loading} error={error}>
                            <ResponsiveNavMenu
                                domains={domains}
                                onClickLink={this.handleResponsiveMenuToggle}
                            />
                        </CoursesMenuContainer>
                    );
                }}
            </LearnMenuTopicsQuery>
        );
    }

    renderResponsiveNavMenu: () => React.ReactNode = () => (
        <div
            role="region"
            aria-label={t`menu`}
            className={css(
                styles.responsiveNavMenuWrapper,
                this.state.responsiveMenuOpen
                    ? styles.responsiveNavMenuWrapperOpen
                    : null,
            )}
            data-testid="responsive-nav-menu"
            id="page-header-responsive-nav-menu"
        >
            <AphroditeCSSTransitionGroup
                transitionStyle={{
                    enter: styles.responsiveNavMenuClosed,
                    enterActive: styles.responsiveNavMenuOpen,
                    leave: styles.responsiveNavMenuOpen,
                    leaveActive: styles.responsiveNavMenuClosed,
                }}
                transitionEnterTimeout={300}
                transitionLeaveTimeout={300}
            >
                {/* NOTE(kevinb): The <View> wrapper here is used as a
                    clipping container as the AphroditeCSSTransitionGroup
                    animates its 'max-height' to show or hide the responsive
                    nav menu inside of it. */}
                <View>{this.maybeRenderResponsiveNavMenu()}</View>
            </AphroditeCSSTransitionGroup>
        </div>
    );

    maybeRenderSupportBar(): React.ReactNode {
        if (!RequestInfo.IS_DEV_SERVER && !PermissionsInfo.hasAnyPermissions) {
            return null;
        }
        if (RequestInfo.isE2E) {
            return null;
        }
        return (
            <React.Suspense fallback={null}>
                <SupportBar />
            </React.Suspense>
        );
    }

    render(): React.ReactNode {
        const {sticky} = this.props;

        return (
            <React.Fragment>
                <div
                    // TODO(FEI-4835): Remove this once OSP (rSAT) has been fully deprecated
                    id="top-header-container"
                    className={css(
                        styles.wrapper,
                        sticky && styles.stickyWrapper,
                        this.isBackgroundWhite() && styles.headerActive,
                    )}
                    style={{backgroundColor: this.getBackgroundColor()}}
                >
                    {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- TODO: Address a11y error */}
                    <nav
                        aria-label={t`page header`}
                        className={css(styles.header)}
                        onFocus={this.handleFocus}
                        onBlur={this.handleBlur}
                        onMouseEnter={this.handleFocus}
                        onMouseLeave={this.handleBlur}
                        role="navigation"
                    >
                        {this.renderProminentSearchContents()}
                        {this.renderResponsiveNavMenu()}
                    </nav>
                </div>
                {this.maybeRenderDonationBanner()}
                {this.maybeRenderSupportBar()}
            </React.Fragment>
        );
    }
}

const NAV_ITEM_DARK_COLOR = color.blue;
const NAV_ITEM_LIGHT_COLOR = color.white;

const SEARCH_ICON_LEFT_MARGIN = spacing.xLarge_32;
const SEARCH_INPUT_LEFT_MARGIN = spacing.large_24;
const PROMINENT_SEARCH_INPUT_LEFT_PADDING = spacing.small_12;

const ACTIVE_SEARCH_BAR_WIDTH = 400;

const styles = StyleSheet.create({
    wrapper: {
        borderTop: `${border.width.thin} solid transparent`, // used to maintain height on active
        borderBottom: `${border.width.thin} solid ${color.white32}`,
        boxSizing: "border-box",
        height: pageHeaderHeight,
        left: 0,
        position: "relative",
        right: 0,
        zIndex: zindexHeader,

        "@media print": {
            // By default, background colors don't print. This style verifies
            // that the dark blue logo stays visible if user enables printing
            // background colors.
            backgroundColor: color.white,
        },
    },

    stickyWrapper: {
        [mediaQueries.mdOrLarger]: {
            position: "sticky",
            top: 0,
        },
    },

    header: {
        maxWidth: maxContainerWidth,
        marginLeft: "auto",
        marginRight: "auto",
        width: "100%",
        height: "100%",
    },

    headerContents: {
        display: "flex",
        height: "100%",
        alignItems: "center",
        "@media print": {
            // Logo should be centered in printable reports
            justifyContent: "center",
        },
    },

    headerActive: {
        borderTop: `${border.width.thin} solid ${color.offBlack16}`,
        borderBottom: `${border.width.thin} solid ${color.offBlack16}`,
        [mediaQueries.smOrSmaller]: {
            background: color.offWhite,
        },
    },

    headerSection: {
        alignItems: "center",
        display: "flex",
        height: "100%",
        columnGap: spacing.xLarge_32,
        [mediaQueries.lgOrSmaller]: {
            columnGap: spacing.medium_16,
        },
    },

    hideOnPrint: {
        "@media print": {
            display: "none",
        },
    },

    learnMenuSpacer: {
        // Account for 10px wide icon + 6px margin between text and icon
        paddingRight: spacing.medium_16,
        pointerEvents: "none",
        visibility: "hidden",
        whiteSpace: "nowrap",
    },

    searchResultsSpacer: {
        marginLeft:
            SEARCH_ICON_LEFT_MARGIN +
            spacing.large_24 + // icon size
            SEARCH_INPUT_LEFT_MARGIN,
    },

    prominentSearchResultsSpacer: {
        marginLeft:
            SEARCH_ICON_LEFT_MARGIN + PROMINENT_SEARCH_INPUT_LEFT_PADDING,
    },

    logo: {
        [mediaQueries.smOrSmaller]: {
            paddingLeft: spacing.medium_16,
        },
    },

    grow: {
        flexGrow: 1,
        flexBasis: 0,
    },

    alignLeft: {
        justifyContent: "flex-start",
        paddingLeft: pageHeaderHorizontalPadding,

        [mediaQueries.smOrSmaller]: {
            display: "none",
        },
    },

    alignRight: {
        justifyContent: "flex-end",
        paddingRight: pageHeaderHorizontalPadding,

        [mediaQueries.smOrSmaller]: {
            paddingRight: 0,
        },
    },

    pad: {
        alignItems: "center",
        display: "flex",
        height: "100%",
    },

    navItem: {
        color: NAV_ITEM_LIGHT_COLOR,
        fontSize: font.size.medium,
        fontWeight: "bold",
    },

    navItemWhenWhite: {
        color: NAV_ITEM_DARK_COLOR,
    },

    textNavItem: {
        ":focus": {
            boxShadow: `inset 0 -3px 0 0 ${NAV_ITEM_LIGHT_COLOR}`,
        },
        ":focus-visible": {
            outline: `${NAV_ITEM_LIGHT_COLOR} solid 1px`,
        },
        whiteSpace: "nowrap",
    },

    textNavItemWhenWhite: {
        ":focus": {
            boxShadow: `inset 0 -3px 0 0 ${NAV_ITEM_DARK_COLOR}`,
        },
        ":focus-visible": {
            outline: `${NAV_ITEM_DARK_COLOR} solid 1px`,
        },
    },

    hoverUnderline: {
        ":hover": {
            textDecoration: "underline",
        },
    },

    noPadding: {
        padding: 0, // fix padding issue on the user dropdown button (white bg)
    },

    searchForm: {
        flexGrow: 1,
    },

    searchInput: {
        border: "none",
        height: "auto",
        fontFamily: font.family.sans,
        fontSize: font.size.medium,
        marginLeft: SEARCH_INPUT_LEFT_MARGIN,
        outline: "none",
    },

    searchWrapper: {
        paddingTop: spacing.small_12,
        paddingBottom: spacing.small_12,
        marginLeft: SEARCH_ICON_LEFT_MARGIN,
        marginRight: SEARCH_ICON_LEFT_MARGIN,
        width: 200,
        boxSizing: "border-box",
    },

    activeSearchWrapper: {
        // We reset the maxWidth to allow the animation to trigger when
        // setting that property using enterSearch/enterActiveSearch.
        maxWidth: "none",
        width: ACTIVE_SEARCH_BAR_WIDTH,
    },

    enterSearch: {
        maxWidth: 200,
        transition: "max-width .3s ease-out",
    },

    enterActiveSearch: {
        maxWidth: ACTIVE_SEARCH_BAR_WIDTH,
    },

    searchBoxContainer: {
        flex: 1,
        paddingLeft: PROMINENT_SEARCH_INPUT_LEFT_PADDING,
        paddingRight: spacing.small_12,
        // This invisible outline is not displayed on normal conditions, but in
        // Windows high contrast mode, it's automatically set to 100% black.
        // That allows the outline of the popup to be visible even though the
        // background color is automatically removed in Window high contrast mode.
        outline: "1px solid rgba(0,0,0,0)",
    },

    prominentSearchInput: {
        height: "100%",
        width: "100%",
        backgroundColor: "transparent",
        marginLeft: 0,
    },

    inactiveSearchButton: {
        justifyContent: "flex-start",
        flexGrow: 1,
    },

    responsiveNavMenuWrapper: {
        background: color.white,
        left: 0,
        position: "absolute",
        right: 0,
        display: "none",

        [mediaQueries.mdOrLarger]: {
            display: "none",
        },
    },

    responsiveNavMenuWrapperOpen: {
        display: "block",
    },

    responsiveNavMenuOpen: {
        maxHeight: 1000,
        transition: "max-height .3s",
        overflow: "hidden",
    },

    responsiveNavMenuClosed: {
        maxHeight: 0,
        transition: "max-height .3s",
        overflow: "hidden",
    },

    responsiveMenuItemWrapper: {
        alignItems: "stretch",
        display: "none",

        [mediaQueries.smOrSmaller]: {
            display: "flex",
        },
    },

    responsiveMenuLink: {
        marginRight: spacing.medium_16,
    },

    rowReverse: {
        // This is used to reverse the order of the elements in search bar so
        // that the search icon is on the left side of the input field and the
        // correct focus order is maintained.
        flexDirection: "row-reverse",
        justifyContent: "flex-end",
    },

    // HACK(kevinb): temporary wonder blocks styles for header only
    // TODO(kevinb): use components from wonder-blocks-typography instead
    wonderBlocksNavItem: typographyStyles.LabelLarge,
});

type ExportProps = WithoutMarkConversion<
    WithoutApollo<
        WithoutUserProfile<
            WithoutLocation<
                JSX.LibraryManagedAttributes<
                    typeof SiteHeader,
                    React.ComponentProps<typeof SiteHeader>
                >
            >
        >
    >
>;

export default compose(
    withUserProfile,
    withLocation,
    withApollo,
    withMarkConversion,
)(SiteHeader) as React.ComponentType<ExportProps>;
